import { useToast } from '@practium/ui';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UpdatePolicy } from '../../api/hooks/policy-versions';
import { Policy } from '../types/policy.types';

interface UsePolicyEditLogicProps {
	updatePolicy: (data: UpdatePolicy) => Promise<Policy>;
	deletePolicy: (policyId: string) => Promise<void>;
	policies: Policy[];
	filteredPolicies: Policy[];
	selectedPolicy: Policy | null;
	selectPolicy: (policy: Policy) => void;
	handleTogglePolicy: (policyId: string, isActive: boolean) => Promise<void>;
	confirm: (options: {
		title: string;
		message: string;
		confirmText?: string;
		cancelText?: string;
		variant?: 'danger' | 'warning' | 'info';
	}) => Promise<boolean>;
}

export const usePolicyEditLogic = ({
	updatePolicy,
	deletePolicy,
	policies,
	selectedPolicy,
	selectPolicy,
	handleTogglePolicy,
	confirm,
}: UsePolicyEditLogicProps) => {
	const navigate = useNavigate();
	const { id: policyId } = useParams<{ id: string }>();
	const [editedPolicy, setEditedPolicy] = useState<Policy | null>(null);
	const [isPreviewMode, setIsPreviewMode] = useState(false);
	const toast = useToast();

	useEffect(() => {
		if (policyId && policies.length > 0) {
			const policy = policies.find((p) => p._id === policyId);
			if (policy) {
				if (selectedPolicy?._id !== policy._id) {
					selectPolicy(policy);
				}
				setEditedPolicy({ ...policy });
				setIsPreviewMode(false);
			}
		}
	}, [policyId, policies, selectPolicy, selectedPolicy?._id]);

	const currentPolicy = editedPolicy || selectedPolicy;

	const handlePolicyChange = useCallback(
		(updatedFields: Partial<Policy>) => {
			if (currentPolicy) {
				const updated = {
					...currentPolicy,
					...updatedFields,
					updatedAt: new Date().toLocaleString('pt-BR'),
				};
				setEditedPolicy(updated);
			}
		},
		[currentPolicy]
	);

	const handleSave = useCallback(async () => {
		if (currentPolicy) {
			try {
				const payload: UpdatePolicy = {
					policyVersionId: currentPolicy._id,
					title: currentPolicy.title,
					description: currentPolicy.description,
					content: currentPolicy.content,
					isActive: currentPolicy.isActive,
					policyType: currentPolicy.policyType,
					versionNumber: currentPolicy.versionNumber,
					effectiveDate: currentPolicy.effectiveDate,
				};
				await updatePolicy(payload);
				navigate(`/politicas/${currentPolicy._id}`);
			} catch (error) {
				if (error instanceof Error) {
					toast.error(error.message);
				}
			}
			navigate(`/politicas/${currentPolicy._id}`);
		}
	}, [currentPolicy, navigate]);

	const handleDelete = useCallback(async () => {
		if (!selectedPolicy) return;

		const confirmed = await confirm({
			title: 'Excluir Política',
			message: `Tem certeza que deseja excluir a política "${selectedPolicy.title || 'sem título'}"? Esta ação não pode ser desfeita.`,
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});

		if (confirmed) {
			try {
				await deletePolicy(selectedPolicy._id);
				navigate('/politicas');
			} catch (error) {
				if (error instanceof Error) {
					toast.error(error.message);
				}
			}
		}
	}, [selectedPolicy, navigate, confirm]);

	const handleVisualize = useCallback(() => {
		setIsPreviewMode((prev) => !prev);
	}, []);

	const handleToggle = useCallback(
		(checked: boolean) => {
			if (currentPolicy) {
				const updated = { ...currentPolicy, isActive: checked };
				setEditedPolicy(updated);
				handleTogglePolicy(currentPolicy._id, checked);
			}
		},
		[currentPolicy, handleTogglePolicy]
	);

	const handleSelectPolicy = useCallback(
		(policy: Policy) => {
			selectPolicy(policy);
			navigate(`/politicas/${policy._id}`);
		},
		[selectPolicy, navigate]
	);

	const handleAddPolicy = useCallback(() => {
		navigate('/politicas/nova');
	}, [navigate]);

	return {
		currentPolicy,
		isPreviewMode,
		handlePolicyChange,
		handleSave,
		handleDelete,
		handleVisualize,
		handleToggle,
		handleSelectPolicy,
		handleAddPolicy,
	};
};
